export type ConnectorType = 'airtable' | 'supabase' | 'stripe' | 'google' | 'custom';

export interface ConnectorConfig {
  id: string;
  type: ConnectorType;
  env: Record<string, string>;
  optionalEnv?: Record<string, string>;
  description?: string;
}

export interface ResolvedConnectorEnv {
  id: string;
  type: ConnectorType;
  values: Record<string, string>;
}

export class MissingConnectorEnvError extends Error {
  public readonly connectorId: string;
  public readonly missing: string[];

  constructor(connectorId: string, missing: string[]) {
    super(`Missing environment variables for connector ${connectorId}: ${missing.join(', ')}`);
    this.name = 'MissingConnectorEnvError';
    this.connectorId = connectorId;
    this.missing = missing;
  }
}

export function createConnectorConfig(config: ConnectorConfig): ConnectorConfig {
  if (!config.id.trim()) throw new Error('Connector id cannot be empty.');
  return config;
}

export function resolveConnectorEnv(
  config: ConnectorConfig,
  env: Record<string, string | undefined>,
): ResolvedConnectorEnv {
  const missing: string[] = [];
  const values: Record<string, string> = {};

  for (const [key, envName] of Object.entries(config.env)) {
    const value = env[envName];
    if (!value) missing.push(envName);
    else values[key] = value;
  }

  if (missing.length > 0) {
    throw new MissingConnectorEnvError(config.id, missing);
  }

  for (const [key, envName] of Object.entries(config.optionalEnv ?? {})) {
    const value = env[envName];
    if (value) values[key] = value;
  }

  return {
    id: config.id,
    type: config.type,
    values,
  };
}

export function summarizeConnectorEnv(config: ConnectorConfig): Array<{
  key: string;
  envName: string;
  required: boolean;
}> {
  return [
    ...Object.entries(config.env).map(([key, envName]) => ({ key, envName, required: true })),
    ...Object.entries(config.optionalEnv ?? {}).map(([key, envName]) => ({
      key,
      envName,
      required: false,
    })),
  ];
}
